import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ExternalLink, FileUp, Link2, Loader2, Trash2 } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { useLang } from "../../i18n/LanguageContext";
import ExpandableText from "./ExpandableText";

// Téléversement de catalogues pour la page « Nos catalogues en ligne » :
//  - PDF (envoyé par morceaux de ~3,5 Mo, miniature PNG de la 1re page créée au serveur)
//  - ou simple LIEN vers un e-catalogue externe (miniature og:image récupérée au mieux)
// La section peut être existante (suggestions) ou nouvelle (taper un nom).
export default function CatalogueUpload() {
  const { t } = useLang();
  const groups = t("catalogues.groups") || [];
  const [mode, setMode] = useState("pdf"); // "pdf" | "lien"
  const [title, setTitle] = useState("");
  const [section, setSection] = useState("");
  const [url, setUrl] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [list, setList] = useState([]);
  const fileRef = useRef(null);

  const load = () => api.get("/admin/custom-catalogues")
    .then(({ data }) => setList(data.catalogues || []))
    .catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sections = [...new Set([...groups.map((g) => g.title), ...list.map((c) => c.section)])];

  const resetForm = () => {
    setTitle(""); setUrl(""); setFile(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const uploadPdf = async () => {
    if (title.trim().length < 2) return toast.error("Titre requis (2 caractères min.)");
    if (!section.trim()) return toast.error("Section requise — choisissez-en une ou tapez un nouveau nom");
    if (!file) return toast.error("Choisissez un fichier PDF");
    if (file.size > 80 * 1024 * 1024) return toast.error("PDF trop volumineux (80 Mo max)");
    setBusy(true);
    setProgress(1);
    try {
      const { data: start } = await api.post("/admin/custom-catalogues/upload/start",
        { filename: file.name, size: file.size, title: title.trim(), section: section.trim() });
      const CHUNK = start.max_chunk;
      const total = Math.ceil(file.size / CHUNK);
      for (let i = 0; i < total; i++) {
        await api.put(`/admin/custom-catalogues/upload/${start.upload_id}/chunk?seq=${i}`,
          file.slice(i * CHUNK, (i + 1) * CHUNK),
          { headers: { "Content-Type": "application/octet-stream" } });
        setProgress(Math.round(((i + 1) / total) * 90));
      }
      setProgress(95);
      await api.post(`/admin/custom-catalogues/upload/${start.upload_id}/complete`);
      setProgress(100);
      toast.success(`Catalogue « ${title.trim()} » publié — miniature créée automatiquement`);
      resetForm();
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Téléversement impossible");
    } finally {
      setBusy(false);
      setTimeout(() => setProgress(0), 1500);
    }
  };

  const addLink = async () => {
    if (title.trim().length < 2) return toast.error("Titre requis (2 caractères min.)");
    if (!section.trim()) return toast.error("Section requise — choisissez-en une ou tapez un nouveau nom");
    if (!/^https?:\/\//.test(url.trim())) return toast.error("Lien invalide (https://… requis)");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/custom-catalogues/link",
        { title: title.trim(), section: section.trim(), url: url.trim() });
      toast.success(data.cover
        ? `E-catalogue « ${title.trim()} » publié — miniature trouvée sur la page`
        : `E-catalogue « ${title.trim()} » publié (pas de miniature trouvée sur la page — la carte s'affichera sans image)`);
      resetForm();
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Ajout impossible");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Retirer « ${c.title} » de la page Nos catalogues en ligne ?`)) return;
    try {
      await api.delete(`/admin/custom-catalogues/${c.id}`);
      setList((xs) => xs.filter((x) => x.id !== c.id));
      toast.success("Catalogue retiré");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const inputCls = "w-full bg-ink border border-ivory/15 rounded-full px-4 py-2.5 text-sm text-ivory placeholder:text-ivory/40 focus:border-gold/60 focus:outline-none transition-colors";

  return (
    <div className="bg-surface border border-gold/15 rounded-xl p-6 mb-8" data-testid="catalogue-upload">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <h3 className="text-xs uppercase tracking-widest text-gold">
          Nos catalogues en ligne — ajouter un catalogue
        </h3>
        <div className="flex gap-2">
          <button onClick={() => setMode("pdf")} data-testid="cat-add-mode-pdf"
            className={`px-4 py-1.5 rounded-full border text-xs transition-colors ${mode === "pdf" ? "bg-gold text-ink border-gold font-bold" : "border-ivory/20 text-ivory/60 hover:border-gold/50 hover:text-gold"}`}>
            Fichier PDF
          </button>
          <button onClick={() => setMode("lien")} data-testid="cat-add-mode-lien"
            className={`px-4 py-1.5 rounded-full border text-xs transition-colors ${mode === "lien" ? "bg-gold text-ink border-gold font-bold" : "border-ivory/20 text-ivory/60 hover:border-gold/50 hover:text-gold"}`}>
            Lien e-catalogue
          </button>
        </div>
      </div>
      <p className="text-xs text-ivory/55 mb-4">
        {mode === "pdf"
          ? "Le PDF est envoyé par morceaux (gros fichiers acceptés, 80 Mo max) et la miniature de la 1re page est créée automatiquement, avec le bouton de partage comme les autres catalogues."
          : "Collez le lien d'un e-catalogue externe (flipbook, site fournisseur…). La miniature est récupérée automatiquement sur la page quand c'est possible."}
      </p>

      <div className="grid md:grid-cols-3 gap-3 mb-3">
        <input data-testid="cat-add-title" value={title} onChange={(e) => setTitle(e.target.value)}
          placeholder="Titre, ex. : Catalogue Hosten 2026" className={inputCls} />
        <div>
          <input data-testid="cat-add-section" value={section} onChange={(e) => setSection(e.target.value)}
            placeholder="Section (existante ou nouvelle)" list="catalogue-sections" className={inputCls} />
          <datalist id="catalogue-sections">
            {sections.map((s) => <option key={s} value={s} />)}
          </datalist>
        </div>
        {mode === "pdf" ? (
          <input ref={fileRef} data-testid="cat-add-file" type="file" accept="application/pdf,.pdf"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="w-full text-xs text-ivory/70 file:mr-3 file:rounded-full file:border-0 file:bg-gold file:text-ink file:font-semibold file:px-4 file:py-2 file:text-xs hover:file:bg-champagne file:transition-colors file:cursor-pointer self-center" />
        ) : (
          <input data-testid="cat-add-url" value={url} onChange={(e) => setUrl(e.target.value)}
            placeholder="https://… (lien de l'e-catalogue)" className={inputCls} />
        )}
      </div>

      {busy && mode === "pdf" && progress > 0 && (
        <div className="mb-3" data-testid="cat-add-progress">
          <div className="h-1.5 bg-ivory/10 rounded-full overflow-hidden">
            <div className="h-full bg-gold transition-all duration-300" style={{ width: `${progress}%` }} />
          </div>
          <p className="text-[11px] text-ivory/55 mt-1">Téléversement… {progress}%</p>
        </div>
      )}

      <button onClick={mode === "pdf" ? uploadPdf : addLink} disabled={busy}
        data-testid="cat-add-submit"
        className="inline-flex items-center gap-2 bg-gold text-ink font-bold px-6 py-2.5 rounded-full text-xs hover:shadow-[0_0_18px_rgba(212,175,55,0.35)] transition-all duration-300 disabled:opacity-50">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (mode === "pdf" ? <FileUp className="w-4 h-4" /> : <Link2 className="w-4 h-4" />)}
        {busy ? "Publication…" : mode === "pdf" ? "Téléverser et publier" : "Publier le lien"}
      </button>

      {list.length > 0 && (
        <div className="mt-6 pt-5 border-t border-ivory/10" data-testid="cat-custom-list">
          <p className="text-[11px] uppercase tracking-widest text-ivory/50 mb-3">
            Catalogues ajoutés ({list.length})
          </p>
          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {list.map((c) => (
              <li key={c.id} data-testid={`cat-custom-${c.id}`}
                className="flex items-center gap-3 border border-ivory/10 rounded-lg p-2.5">
                {c.cover_url ? (
                  <img src={c.cover_url} alt="" className="w-10 h-13 object-cover object-top rounded shrink-0" style={{ height: 52 }} />
                ) : (
                  <span className="w-10 rounded bg-ink border border-ivory/10 flex items-center justify-center shrink-0" style={{ height: 52 }}>
                    {c.url ? <Link2 className="w-4 h-4 text-gold/60" /> : <FileUp className="w-4 h-4 text-gold/60" />}
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  <ExpandableText text={c.title} testId={`cat-custom-title-${c.id}`} className="block text-xs font-semibold text-ivory" />
                  <ExpandableText text={`${c.section} · ${c.url ? "lien externe" : `PDF · ${c.pages} p. · ${(c.size / 1048576).toFixed(1)} Mo`}`}
                    testId={`cat-custom-meta-${c.id}`} className="block text-[11px] text-ivory/50" />
                  <a href={`/catalogue-viewer?src=${encodeURIComponent(c.url || c.file_url)}&nom=${encodeURIComponent(c.title)}`}
                    target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1 text-[11px] text-gold/80 hover:text-gold transition-colors">
                    <ExternalLink className="w-3 h-3" /> Voir
                  </a>
                </div>
                <button onClick={() => remove(c)} data-testid={`cat-custom-delete-${c.id}`}
                  title="Retirer de la page"
                  className="shrink-0 p-2 text-ivory/40 hover:text-red-400 transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
